import type { CollectionMetadata } from '@/types'

// Day counts for the age filter; 'custom' lets the admin type their own.
export const AGE_FILTER_PRESETS = ['30', '60', '90', '180', '365', 'custom'] as const

const CREATED = ['createdAt', 'created_at']
const UPDATED_THEN_CREATED = ['updatedAt', 'updated_at', 'createdAt', 'created_at']

/**
 * Collections the Documents page can clean up. Field spellings list the camelCase
 * name the apps write first and the legacy snake_case one after
 * (CROSS_PLATFORM_CONTRACTS.md), so documents of either generation are found.
 */
export const COLLECTION_METADATA: CollectionMetadata[] = [
  {
    name: 'users',
    hasAuth: true,
    timestampFields: CREATED,
    displayFields: [
      { label: 'email', keys: ['email'] },
      { label: 'name', keys: ['name', 'displayName', 'display_name', 'first_name'] },
      { label: 'role', keys: ['role'] },
      { label: 'createdAt', keys: CREATED },
      { label: 'isActive', keys: ['isActive', 'is_active'] },
    ],
    canBulkDelete: true,
  },
  {
    name: 'businesses',
    hasAuth: false,
    timestampFields: CREATED,
    displayFields: [
      { label: 'name', keys: ['name'] },
      { label: 'ownerId', keys: ['ownerId', 'owner_id', 'owner_user_id'] },
      { label: 'createdAt', keys: CREATED },
      { label: 'isActive', keys: ['isActive', 'is_active'] },
    ],
    // Deleting a circle document directly would strand its members, contacts and
    // requests. A circle is closed by deleting the care recipient who owns it
    // (Users → Delete), which removes the circle and everything in it.
    canBulkDelete: false,
  },
  {
    name: 'subscription_packages',
    hasAuth: false,
    timestampFields: CREATED,
    displayFields: [
      { label: 'name', keys: ['name'] },
      { label: 'monthly_price_eur', keys: ['monthly_price_eur'] },
      { label: 'created_at', keys: CREATED },
    ],
    canBulkDelete: false,
  },
  {
    name: 'usage_analytics',
    hasAuth: false,
    timestampFields: ['timestamp', 'createdAt', 'created_at'],
    displayFields: [
      { label: 'businessId', keys: ['businessId', 'business_id'] },
      { label: 'date', keys: ['date'] },
      { label: 'timestamp', keys: ['timestamp', 'createdAt', 'created_at'] },
    ],
    canBulkDelete: true,
  },
  {
    name: 'usage_events',
    hasAuth: false,
    timestampFields: ['timestamp'],
    displayFields: [
      { label: 'userId', keys: ['userId', 'user_id'] },
      { label: 'eventType', keys: ['eventType', 'event_type', 'event_name'] },
      { label: 'timestamp', keys: ['timestamp'] },
    ],
    canBulkDelete: true,
  },
  {
    name: 'global_features',
    hasAuth: false,
    timestampFields: UPDATED_THEN_CREATED,
    displayFields: [
      { label: 'businessId', keys: ['businessId', 'business_id'] },
      { label: 'enabled_features', keys: ['enabled_features', 'enabledFeatures'] },
      { label: 'updatedAt', keys: UPDATED_THEN_CREATED },
    ],
    canBulkDelete: false,
  },
  {
    name: 'user_features',
    hasAuth: false,
    timestampFields: UPDATED_THEN_CREATED,
    displayFields: [
      { label: 'userId', keys: ['userId', 'user_id'] },
      { label: 'enabled_features', keys: ['enabled_features', 'enabledFeatures'] },
      { label: 'updatedAt', keys: UPDATED_THEN_CREATED },
      { label: 'isActive', keys: ['isActive', 'is_active'] },
    ],
    canBulkDelete: true,
  },
]

export function getCollectionMetadata(collectionName: string): CollectionMetadata | undefined {
  return COLLECTION_METADATA.find(c => c.name === collectionName)
}
